//src/services/riskInventories.ts
import { api } from "@/lib/apiClient";
import type { AiFeedbackStatus, AiGeneratedOutput, AiGeneratedOutputPage } from "@/types/ai";
import type {
  PagedInventoryItems,
  RiskInventory,
  RiskInventoryActionPlanModel,
  RiskInventoryActionPlanResponsibleUser,
  RiskInventoryCreateIn,
  RiskInventoryDetail,
  RiskInventoryItem,
  RiskInventoryItemActionResult,
  RiskInventoryItemCreateIn,
  RiskInventoryItemFormOptions,
  RiskInventoryItemLog,
  RiskInventoryItemStatusIn,
  RiskInventoryItemUpdateIn,
  RiskInventoryLinkActionPlanIn,
  RiskInventoryOperationalActionPlan,
  RiskInventoryOperationalActionPlanDetail,
  RiskInventoryOperationalActionPlanItem,
  RiskInventoryOperationalActionPlanItemCreateIn,
  RiskInventoryOperationalActionPlanItemUpdateIn,
  RiskInventoryOperationalActionPlanLinkResult,
  RiskInventoryOperationalActionPlanUnlinkResult,
  RiskInventoryStatusIn,
  RiskInventoryUpdateIn,
} from "@/types/riskInventory";

export async function listRiskInventories(params?: { status?: string; unit_id?: string }) {
  return api.get<RiskInventory[]>("/risk-inventories", { params });
}
export async function getRiskInventory(id: string) {
  return api.get<RiskInventoryDetail>(`/risk-inventories/${id}`);
}
export async function getRiskInventoryItemFormOptions(inventoryId: string) {
  return api.get<RiskInventoryItemFormOptions>(`/risk-inventories/${inventoryId}/item-form-options`);
}
export async function listRiskInventoryItemLogs(itemId: string) {
  return api.get<RiskInventoryItemLog[]>(`/risk-inventories/items/${itemId}/logs`);
}
export async function createRiskInventory(payload: RiskInventoryCreateIn) {
  return api.post<RiskInventory>("/risk-inventories", payload);
}
export async function updateRiskInventory(inventoryId: string, payload: RiskInventoryUpdateIn) {
  return api.put<RiskInventory>(`/risk-inventories/${inventoryId}`, payload);
}
export async function setRiskInventoryStatus(inventoryId: string, payload: RiskInventoryStatusIn) {
  return api.patch<RiskInventory>(`/risk-inventories/${inventoryId}/status`, payload);
}
export async function listRiskInventoryItems(
  inventoryId: string,
  params?: { status?: string; dimension_id?: string; page?: number; per_page?: number },
) {
  return api.get<PagedInventoryItems>(`/risk-inventories/${inventoryId}/items`, {
    params: {
      status: params?.status || undefined,
      dimension_id: params?.dimension_id || undefined,
      page: params?.page || 1,
      per_page: params?.per_page || 10,
    },
  });
}
export async function createRiskInventoryItem(inventoryId: string, payload: RiskInventoryItemCreateIn) {
  return api.post<RiskInventoryItem>(`/risk-inventories/${inventoryId}/items`, payload);
}
export async function updateRiskInventoryItem(itemId: string, payload: RiskInventoryItemUpdateIn) {
  return api.put<RiskInventoryItem>(`/risk-inventories/items/${itemId}`, payload);
}
export async function setRiskInventoryItemStatus(itemId: string, payload: RiskInventoryItemStatusIn) {
  return api.patch<RiskInventoryItem>(`/risk-inventories/items/${itemId}/status`, payload);
}
export async function deleteRiskInventoryItem(itemId: string) {
  return api.delete<RiskInventoryItemActionResult>(`/risk-inventories/items/${itemId}`);
}
export async function removeRiskInventoryItemFromInventory(itemId: string) {
  return api.post<RiskInventoryItemActionResult>(`/risk-inventories/items/${itemId}/remove-from-inventory`, {});
}

export async function listSuggestedActionPlanModels(itemId: string) {
  return api.get<RiskInventoryActionPlanModel[]>(`/risk-inventories/items/${itemId}/action-plans/suggested`);
}

export async function searchActionPlanModels(itemId: string, q?: string) {
  return api.get<RiskInventoryActionPlanModel[]>(`/risk-inventories/items/${itemId}/action-plans/search`, {
    params: { q: q?.trim() || undefined },
  });
}


export async function listInventoryActionPlanResponsibleUsers(inventoryId: string) {
  return api.get<RiskInventoryActionPlanResponsibleUser[]>(`/risk-inventories/${inventoryId}/action-plan-users`);
}

export async function listInventoryItemActionPlans(itemId: string) {
  return api.get<RiskInventoryOperationalActionPlan[]>(`/risk-inventories/items/${itemId}/action-plans`);
}

export async function linkActionPlanModelToInventoryItem(itemId: string, payload: RiskInventoryLinkActionPlanIn) {
  return api.post<RiskInventoryOperationalActionPlanLinkResult>(`/risk-inventories/items/${itemId}/action-plans`, payload);
}

export async function unlinkInventoryItemActionPlan(itemId: string, planId: string) {
  return api.delete<RiskInventoryOperationalActionPlanUnlinkResult>(`/risk-inventories/items/${itemId}/action-plans/${planId}`);
}

export async function getInventoryItemActionPlan(itemId: string, planId: string) {
  return api.get<RiskInventoryOperationalActionPlanDetail>(`/risk-inventories/items/${itemId}/action-plans/${planId}`);
}

export async function createInventoryItemActionPlanItem(
  itemId: string,
  planId: string,
  payload: RiskInventoryOperationalActionPlanItemCreateIn,
) {
  return api.post<RiskInventoryOperationalActionPlanItem>(`/risk-inventories/items/${itemId}/action-plans/${planId}/items`, payload);
}

export async function updateInventoryItemActionPlanItem(
  itemId: string,
  planId: string,
  planItemId: string,
  payload: RiskInventoryOperationalActionPlanItemUpdateIn,
) {
  return api.put<RiskInventoryOperationalActionPlanItem>(
    `/risk-inventories/items/${itemId}/action-plans/${planId}/items/${planItemId}`,
    payload,
  );
}

export async function setInventoryItemActionPlanItemStatus(
  itemId: string,
  planId: string,
  planItemId: string,
  status: RiskInventoryOperationalActionPlanItem["status"],
) {
  return api.patch<RiskInventoryOperationalActionPlanItem>(
    `/risk-inventories/items/${itemId}/action-plans/${planId}/items/${planItemId}/status`,
    { status },
  );
}

export async function deleteInventoryItemActionPlanItem(itemId: string, planId: string, planItemId: string) {
  return api.delete<RiskInventoryItemActionResult>(`/risk-inventories/items/${itemId}/action-plans/${planId}/items/${planItemId}`);
}


export async function generateRiskInventoryAiInsights(inventoryId: string) {
  return api.post<AiGeneratedOutput>(`/risk-inventories/${inventoryId}/ai/insights`, {});
}

export async function listRiskInventoryAiInsights(inventoryId: string, params?: { page?: number; per_page?: number }) {
  return api.get<AiGeneratedOutputPage>(`/risk-inventories/${inventoryId}/ai/insights`, {
    params: {
      page: params?.page || 1,
      per_page: params?.per_page || 10,
    },
  });
}


export async function updateRiskInventoryAiInsightFeedback(
  inventoryId: string,
  outputId: string,
  feedbackStatus: AiFeedbackStatus,
) {
  return api.patch<AiGeneratedOutput>(`/risk-inventories/${inventoryId}/ai/insights/${outputId}/feedback`, {
    feedback_status: feedbackStatus,
  });
}
